import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authCheckedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    // Only check auth once on initial mount to prevent re-auth loops
    if (authCheckedRef.current) {
      return;
    }
    
    const token = localStorage.getItem('verlo_token');
    const refresh = localStorage.getItem('verlo_refresh');
    const storedUser = localStorage.getItem('verlo_user');

    // A refresh token alone is enough to come back: the api interceptor turns the
    // first 401 into a silent refresh + replay (auth item 7).
    if (token || refresh) {
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser)); // paint from cache to avoid a flash
        } catch (e) {
          // Invalid stored user
        }
      }

      try {
        const response = await authApi.getMe();
        setUser(response.data);
        localStorage.setItem('verlo_user', JSON.stringify(response.data));
      } catch (err) {
        // Only clear on explicit 401 (a failed refresh lands here too), not network errors
        if (err.response?.status === 401) {
          localStorage.removeItem('verlo_token');
          localStorage.removeItem('verlo_user');
          localStorage.removeItem('verlo_refresh');
          setUser(null);
        }
        // On other errors, keep user logged in with stored data
      }
    }
    
    authCheckedRef.current = true;
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep a session the API just issued, then fetch the enriched /me so RBAC fields are present.
  const storeSession = async ({ access_token, refresh_token, user: userData }) => {
    localStorage.setItem('verlo_token', access_token);
    if (refresh_token) localStorage.setItem('verlo_refresh', refresh_token);
    try {
      const me = await authApi.getMe();
      localStorage.setItem('verlo_user', JSON.stringify(me.data));
      setUser(me.data);
      return me.data;
    } catch {
      localStorage.setItem('verlo_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    }
  };

  // A login answers with a session, or with a step to finish first (#179): `{ step,
  // challenge_token, ... }` — a 2FA code, setting up an authenticator (Verlo's staff must have
  // one), or a new password in place of an invite's temporary one. A step comes back to the
  // caller as it is; a session is stored and the user returned, as it always was.
  const settle = async (data) => (data.step ? data : storeSession(data));

  const failure = (err, fallback) => {
    const message = err.response?.data?.detail || fallback;
    setError(message);
    const wrapped = new Error(message);
    wrapped.status = err.response?.status;
    return wrapped;
  };

  const login = async (email, password, remember = false) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password, remember });
      return await settle(response.data);
    } catch (err) {
      throw failure(err, 'Login failed');
    }
  };

  /** Answer the step `login` returned: `{ code }` or `{ new_password }`. Settles the same way. */
  const respondToStep = async (challengeToken, answer) => {
    setError(null);
    try {
      const response = await authApi.loginRespond({ challenge_token: challengeToken, ...answer });
      return await settle(response.data);
    } catch (err) {
      throw failure(err, 'Sign-in failed');
    }
  };

  const logout = () => {
    // Clear local state — and let every `logout(); navigate(...)` caller proceed —
    // *before* touching the network. This used to await the server call first, which
    // on a slow or unreachable node-api (no timeout, and it doesn't mount
    // `/auth/logout` at all — always a 404) meant the old user was still in state when
    // the PIN screen mounted and bounced straight back to their landing page, and the
    // kiosk's 90-second idle lock didn't lock.
    const token = localStorage.getItem('verlo_token');
    const hadRefresh = Boolean(localStorage.getItem('verlo_refresh'));
    localStorage.removeItem('verlo_token');
    localStorage.removeItem('verlo_user');
    localStorage.removeItem('verlo_refresh');
    setUser(null);
    // Tell the server to end this session (item 7) — fire-and-forget. Skipped with no
    // refresh token: that's the kiosk/PIN path, where this endpoint doesn't exist.
    if (hadRefresh && token) {
      authApi.logout(token).catch(() => { /* offline / already gone */ });
    }
  };

  /** Verlo's own staff (#180), inside a restaurant's portal. They belong to no org; `/auth/me`
   *  gives them this one's `org_id` only while they are in it with `orgs.assist`, and for
   *  that long they are its owner. Outside a portal they are in no restaurant at all. */
  const isVerloStaffInPortal = () => !!user?.platform_role && !!user?.org_id;

  const isAdmin = () => {
    return user?.role === 'admin' || isVerloStaffInPortal();
  };

  /** RBAC v2 — true if the user has the given permission key */
  const hasPermission = (perm) => {
    if (!user) return false;
    // Owner short-circuit even in legacy mode
    if (user.rbac_role === 'owner') return true;
    const perms = user.effective_permissions;
    if (!perms) {
      // Legacy fallback while we wait for /me to enrich the cached user
      return user.role === 'admin';
    }
    return perms.includes(perm);
  };

  /** Verlo's own staff (#180) — true if the account's platform role holds this permission.
   *  A separate list from `effective_permissions`: what they may do to the platform, not
   *  inside a restaurant. Always false for a restaurant's own account. */
  const hasPlatformPermission = (perm) => !!user?.platform_permissions?.includes(perm);

  const landingPath = () => user?.default_landing_path || '/tables';

  const value = {
    user,
    loading,
    error,
    login,
    respondToStep,
    logout,
    isAdmin,
    hasPermission,
    hasPlatformPermission,
    landingPath,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
